import type {
  SchedulerNode as SchedulerNodeType,
  ConversationNode,
  SchedulerDatum,
} from 'src/types/chatbot';
import type {
  AdditionalSchedulerQuestion,
  SchedulerAnswerType,
} from 'src/types/scheduler';
import type {Validation} from 'src/utils/validation';
import type {EntityType} from 'src/types/ats-entities';
import type {NodeEditorProps} from './node-editor-v2';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import classify from 'src/utils/classify';
import {useSelector, useDispatch} from 'react-redux';
import lodashKeyBy from 'lodash/keyBy';

import * as api from 'src/utils/api-no-store';
import {useReleaseFlag} from 'src/hooks/product-flags';
import {useMutableFieldStatus} from '../api-hooks';

import {fromArray, keyBy} from 'src/utils/map';

import {useChatbotSchedulerTemplates} from 'src/hooks/api/scheduler';

import {useChatbotStateContext} from '../chatbot-state-context';

import {
  createSchedulerNode,
  createEditableGraph,
  findAncestorNodeVariables,
  validationTypeByQuestionType,
  getQuestionText,
  getTemplateTypeOptions,
  getHasAncestorJobMatchQuestionNode,
  getIsUsingDiscoverAttribute,
} from '../utils';
import {useFirstSmsMessage} from '../node-editor/common';

import {createRawDraftContentState, hasText} from 'src/utils/draft';

import {getAllNamespacedGlobalVariables} from 'src/action-creators/namespaced-global-variables';

import {validateNode} from '../node-editor/validation';
import {filterValidation} from 'src/utils/validation';

import {Button} from 'src/designSystem2021Components/buttons';

import VariablePickerSelect from 'src/components/lib/variable-picker/select-2';

import {
  MessageDraftEditor,
  upgradePossibleEntitiesInRDCS,
} from 'src/components/conversation-builder/draft-editor';

import {SimpleDropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import {CloseIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Toggle as GenesisToggle} from '@spaced-out/ui-design-system/lib/components/Toggle';

import {Input} from 'src/designSystem2021Components/input';
import {Select} from 'src/designSystem2021Components/select';
import {AllVariablePicker} from '../node-editor/variable-picker';
import {Toggle} from '../node-editor/node-link-toggle';
import Loading from 'src/components/lib/loading';

import {
  useEditorState,
  useAtsVariables,
  useChatbotVariables,
} from '../node-editor/reducer';

import css from './node-editor-v2.css';
import {selectEntityTypeMappingsByName} from 'src/selectors/ats-entities';
import RichEditorWithWarning from 'src/components/conversation-builder/lib/rich-editor-with-warning/rich-editor-with-warning';
import {getDiscoverAttributeWarningMessage} from 'src/components/conversation-builder/constants';
import {type RawDraftContentState, type DraftDecoratorType} from 'draft-js';


type VariableTemplateStatus = 'loading' | 'success' | 'error' | undefined;

export default function SchedulerNode({
  graph,
  onCancel,
  readOnly,
  isSavingNode,
  setIsSavingNode,
}: NodeEditorProps) {
  const {t, makeKey} = useLabelTranslation();
  const richTextEditorEnabled = useReleaseFlag('chatbotFlowRichEditor');
  const richTextEditorFormattingEnabled = useReleaseFlag(
    'chatbotFlowRichEditorFormatting',
  );
  const chatbotNewHyperlinkModalEnabled = useReleaseFlag(
    'chatbotNewHyperlinkModal',
  );
  const atsMappings = useSelector(selectEntityTypeMappingsByName);
  const {state, dispatch} = useChatbotStateContext();
  const reduxDispatch = useDispatch();

  const chatbotMultiHostMeeting = useReleaseFlag('chatbotMultiHostMeeting');

  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  const schedulerNodeEnhancementEnabled = useReleaseFlag(
    'chatbotSchedulerNodeEnhancement',
  );
  const allowMeetingNodeVariables = Boolean(
    useReleaseFlag('chatbotMeetingNodeVariable'),
  );

  const allowMeetingBranching = Boolean(
    useReleaseFlag('chatbotMeetingBranching'),
  );

  const editingNodeData = graph.nodes.find(({id}) => id === state.editingNode);
  const defaultNode =
    state.editingNode !== null && editingNodeData?.type === 'scheduler'
      ? editingNodeData
      : createSchedulerNode(t, allowMeetingBranching);

  const {isFieldReadOnly, canFlowUpdate} = useMutableFieldStatus(
    readOnly,
    defaultNode.type,
  );

  const edgeId = Number(state.insertingNode?.edgeId || state.editingNode);

  const [editorState, editorDispatch] = useEditorState<SchedulerNodeType>({
    defaultNode,
  });

  const {node} = editorState;

  const setNode = (payload: SchedulerNodeType) => {
    editorDispatch({type: 'update', payload});
  };

  const editableGraph = createEditableGraph(graph);
  const isFirstSmsMessage = useFirstSmsMessage(editableGraph, defaultNode);

  const nodeVariables = findAncestorNodeVariables(
    editableGraph,
    defaultNode,
    edgeId,
    false,
    false,
    allowMeetingNodeVariables,
  );
  const mappedNodeVariables = nodeVariables.map((nodeVar) => ({
    ...nodeVar,
    key: nodeVar.node_id,
    label: nodeVar.name,
  }));

  const hasAncestorJobMatchQuestionNode = getHasAncestorJobMatchQuestionNode(
    editableGraph,
    defaultNode,
    state.insertingNode?.edgeId,
  );

  const chatbotDynamicLabels = useChatbotVariables(graph.anchor_entity_type, {
    showDiscoverAttributes: hasAncestorJobMatchQuestionNode,
    showExternalVariables: Boolean(graph.flow_config.api_bot_enabled),
  });

  const validation = validateNode(node, editableGraph);
  const error = hasSubmitted ? validation : null;
  const messageError = filterValidation(error, ['text_draft_json']);
  const calendarError = filterValidation(error, [
    'scheduler_template_id',
    'scheduler_template_questions',
    'scheduler_template_additional_questions',
    'scheduler_mapping_var',
  ]);
  const detailsError = filterValidation(error, ['event_info']);

  const templates = useChatbotSchedulerTemplates();
  // TODO: on slower speeds, not all templates are loaded at the same time. We should consider showing a loading spinner until all templates are fetched to avoid any unintended consequences here
  const templatesMap = React.useMemo(() => fromArray(templates), [templates]);

  const selectedTemplateDistributionRule = node.scheduler_template_id
    ? templatesMap.get(node.scheduler_template_id)?.distribution_rule
    : 'panel_interview';

  const [distributionFilter, setDistributionFilter] = React.useState(
    selectedTemplateDistributionRule ?? 'panel_interview',
  );

  const filteredTemplates = templates.filter(
    ({distribution_rule}) =>
      !chatbotMultiHostMeeting ||
      distributionFilter.includes(distribution_rule),
  );

  const nodes = React.useMemo(
    () => [...graph.nodes.values()].filter((node) => node.type === 'question'),
    [graph.nodes],
  );

  const labels = useAtsVariables(graph.anchor_entity_type);

  const dynamicLabels = React.useMemo(
    () => [
      ...labels.filter(
        (label) =>
          label.mapping.is_internal === true &&
          (label.entityRelationshipPath[0] === 'id' ||
            label.attributeName === 'id'),
      ),
    ],
    [labels],
  );

  // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
  const dynamicLabelMap = React.useMemo(() => keyBy(dynamicLabels, 'value'));
  // For Genesis
  const normalizedDynamicLabels = React.useMemo(
    () => [
      ...dynamicLabels.map((label) => ({
        key: label.value,
        label: t(makeKey(label.name), label.name),
      })),
    ],
    [labels],
  );

  const isSourcingFlow = graph.use_case === 'sourcing';

  React.useEffect(() => {
    if (node.scheduler_template_id !== defaultNode.scheduler_template_id) {
      setNode({
        ...node,
        scheduler_data: {
          ...node.scheduler_data,
          additional_questions:
            node.scheduler_template_id != null
              ? templatesMap
                  .get(node.scheduler_template_id)
                  ?.additional_questions.map(
                    (q: AdditionalSchedulerQuestion) => ({
                      dynamic_field: null,
                      node_id: null,
                      name: q.question,
                    }),
                  ) ?? []
              : [],
        },
      });
    }
  }, [node.scheduler_template_id]);

  React.useEffect(() => {
    if (schedulerNodeEnhancementEnabled) {
      reduxDispatch(getAllNamespacedGlobalVariables());
      if (!node.scheduler_data.type) {
        setNode({
          ...node,
          scheduler_data: {
            ...node.scheduler_data,
            type: 'static_template',
          },
        });
      }
    }
  }, []);

  React.useEffect(() => {
    setDistributionFilter(
      // @ts-expect-error - TS2345 - Argument of type 'SchedulerDistributionRuleType | "panel-interview"' is not assignable to parameter of type 'SetStateAction<"single_account" | "round_robin_availability" | "round_robin_fairness" | "panel_interview" | "one_event_multiple_attendee" | "one_slot_multiple_event">'.
      selectedTemplateDistributionRule ?? 'panel-interview',
    );
  }, [selectedTemplateDistributionRule]);

  const globalVariables =
    useSelector((state) =>
      state.namespacedGlobalVariables?.filter(
        // @ts-expect-error - TS7031 - Binding element 'anchor_entity_type' implicitly has an 'any' type.
        ({anchor_entity_type}) =>
          anchor_entity_type === graph.anchor_entity_type,
      ),
    ) ?? [];

  const mappedGloablVariables = globalVariables.map(
    // @ts-expect-error - TS7031 - Binding element 'fully_formed_name' implicitly has an 'any' type. | TS7031 - Binding element 'global_variable' implicitly has an 'any' type.
    ({fully_formed_name, global_variable}) => ({
      ...global_variable,
      value: global_variable.id,
      label: fully_formed_name,
    }),
  );
  // const globalVariablesById = lodashKeyBy(mappedGloablVariables, 'id');
  const globalVariablesByLabel = lodashKeyBy(mappedGloablVariables, 'label');

  const showQuestionsForStatic =
    node.scheduler_data.type === 'static_template' &&
    node.scheduler_template_id;
  const showQuestionsForDynamic =
    node.scheduler_data.type === 'dynamic_template' &&
    node.scheduler_data.global_variable;
  const showTemplateQuestions =
    !schedulerNodeEnhancementEnabled ||
    (schedulerNodeEnhancementEnabled &&
      (showQuestionsForStatic || showQuestionsForDynamic));

  const [variableTemplateStatus, setVariableTemplateStatus] =
    React.useState<VariableTemplateStatus>();

  const handleSelectGloablVariable = (selected: any | null | undefined) => {
    if (selected) {
      setVariableTemplateStatus('loading');

      api
        .get(`scheduler/event-template/${selected.value}/additional-questions`)
        .then((res) => {
          setNode({
            ...node,
            scheduler_data: {
              ...node.scheduler_data,
              // @ts-expect-error - TS7031 - Binding element 'template_ids' implicitly has an 'any' type. | TS7031 - Binding element 'question' implicitly has an 'any' type.
              additional_questions: res.map(({template_ids, question}) => ({
                node_id: null,
                dynamic_field: null,
                template_ids,
                name: question,
              })),
              global_variable: {
                id: selected.value,
                name: selected.label,
              },
            },
          });
          setVariableTemplateStatus('success');
        })
        .catch((err) => setVariableTemplateStatus('error'));
    }
  };

  const templateTypeOptions = getTemplateTypeOptions(t);

  const templateOptions = React.useMemo(
    () =>
      graph.use_case === 'sourcing'
        ? templateTypeOptions.filter(({value}) => value === 'static_template')
        : templateTypeOptions,
    [],
  );

  const isSelectedTemplatePanel =
    chatbotMultiHostMeeting &&
    node.scheduler_template_id &&
    [
      'panel_interview',
      'round_robin_availability',
      'round_robin_fairness',
      // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    ].includes(templatesMap.get(node.scheduler_template_id)?.distribution_rule);
  const primaryPanelAttendees = node.scheduler_data.contacts.filter(({name}) =>
    name.includes('primary'),
  );
  const secondaryPanelAttendees = node.scheduler_data.contacts.filter(
    ({name}) => name.includes('secondary'),
  );

  const handleUpdateContact = (
    // @ts-expect-error - TS7006 - Parameter 'index' implicitly has an 'any' type.
    index,
    fieldName: string,
    attendeeType: string,
    // @ts-expect-error - TS7006 - Parameter 'isPrimary' implicitly has an 'any' type.
    isPrimary,
  ) => {
    const newPrimaryPanelAttendees = node.scheduler_data.contacts.filter(
      ({name}) => name.includes('primary'),
    );
    const newSecondaryPanelAttendees = node.scheduler_data.contacts.filter(
      ({name}) => name.includes('secondary'),
    );

    const attendeeListToUpdate = isPrimary
      ? newPrimaryPanelAttendees
      : newSecondaryPanelAttendees;

    if (typeof index === 'number') {
      attendeeListToUpdate[index] = {
        dynamic_field: fieldName,
        name: attendeeType,
        node_id: null,
      };
    } else {
      attendeeListToUpdate.push({
        dynamic_field: fieldName,
        name: attendeeType,
        node_id: null,
      });
    }

    setNode({
      ...node,
      scheduler_data: {
        ...node.scheduler_data,
        contacts: [...newPrimaryPanelAttendees, ...newSecondaryPanelAttendees],
      },
    });
  };

  // @ts-expect-error - TS7006 - Parameter 'index' implicitly has an 'any' type.
  const handleRemoveAttendee = (index) => {
    const newContactList = [...node.scheduler_data.contacts];
    if (index !== null) {
      newContactList.splice(index, 1);
    }
    setNode({
      ...node,
      scheduler_data: {
        ...node.scheduler_data,
        contacts: newContactList,
      },
    });
  };

  const handleAddPanelAttendee = (isPrimary: boolean) => {
    setNode({
      ...node,
      scheduler_data: {
        ...node.scheduler_data,
        contacts: [
          ...node.scheduler_data.contacts,
          {
            dynamic_field: null,
            name: isPrimary ? 'primary_panel_host' : 'secondary_panel_host',
            node_id: null,
          },
        ],
      },
    });
  };

  const prepareContentOnLoad = (
    rdcs: RawDraftContentState,
    decorator: DraftDecoratorType,
  ) =>
    upgradePossibleEntitiesInRDCS(
      rdcs,
      editableGraph,
      nodeVariables,
      dynamicLabels,
      decorator,
    );

  const handleSaveNode = () => {
    if (validation) {
      setHasSubmitted(true);
    } else {
      state.insertingNode
        ? dispatch({
            type: 'COMMIT_NODE',
            payload: node,
          })
        : dispatch({
            type: 'UPDATE_NODE',
            payload: node,
          });
    }
  };

  React.useEffect(() => {
    if (isSavingNode) {
      handleSaveNode();
      setIsSavingNode(false);
    }
  }, [isSavingNode]);

  const isUsingDiscoverAttribute = getIsUsingDiscoverAttribute(
    node.text_draft_json,
  );

  const isUsingDiscoverAttributeInTitle = node.scheduler_data.event_info
    ?.event_title
    ? getIsUsingDiscoverAttribute(node.scheduler_data.event_info.event_title)
    : false;

  const isUsingDiscoverAttributeInDetails = node.scheduler_data.event_info
    ?.event_description
    ? getIsUsingDiscoverAttribute(
        node.scheduler_data.event_info.event_description,
      )
    : false;

  const meetingDetailsError =
    error?.event_info &&
    (!node.scheduler_data.event_info?.event_description ||
      (node.scheduler_data.event_info.event_description &&
        !hasText(node.scheduler_data.event_info.event_description)))
      ? error.event_info
      : undefined;
  let meetingDetailsErrorMessage;

  if (meetingDetailsError?.errors?.includes('BAD_VARIABLE')) {
    meetingDetailsErrorMessage = `${t(
      'Variables_highlighted_in_red_are_invalid',
      'Variables highlighted in red are invalid',
    )}.`;
  } else if (meetingDetailsError?.errors?.includes('EMPTY')) {
    meetingDetailsErrorMessage = `${t(
      'Meeting_details_cannot_be_empty',
      'Meeting details cannot be empty',
    )}.`;
  } else if (meetingDetailsError?.errors) {
    meetingDetailsErrorMessage = t(
      'Field_cannot_be_empty',
      'Field cannot be empty',
    );
  }

  const distributionRuleTypes = [
    {value: 'panel_interview', label: t('Panel_Interview', 'Panel Interview')},
    {
      value: 'round_robin_availability_round_robin_fairness',
      label: t('Round_Robin', 'Round Robin'),
    },
    {value: 'one_slot_multiple_event', label: t('MultiEvent', 'Multi-Event')},
  ];

  const DISCOVER_ATTRIBUTE_WARNING_MESSAGE =
    getDiscoverAttributeWarningMessage(t);

  return (
    <div className={css.questionNodeContainer}>
      <div className={css.section}>
        <Input
          label={t('Node_Title', 'Node Title')}
          value={node.name}
          onChange={(event) =>
            setNode({...node, name: event.currentTarget.value})
          }
        />
        {error?.name && (
          <div className={css.errorText}>
            {error.name
              ? error.name.errors?.includes('EMPTY')
                ? `${t(
                    'This_node_must_have_a_name',
                    'This node must have a name',
                  )}.`
                : `${t(
                    'This_name_is_already_taken',
                    'This name is already taken',
                  )}.`
              : `${t(
                  'Node_names_must_be_unique_40_characters_maximum',
                  'Node names must be unique. 40 characters maximum',
                )}.`}
          </div>
        )}
      </div>
      <div className={css.collapsibleHeader}>{t('Message', 'Message')}</div>
      <div className={classify(css.section, css.withMargin)}>
        <div className={css.inputLabel}>{`${t('Message', 'Message')}*`}</div>
        {richTextEditorEnabled ? (
          <RichEditorWithWarning
            warningMessage={
              isUsingDiscoverAttribute ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE : ''
            }
            defaultValue={node.text_draft_json}
            onChange={(text_draft_json) => {
              setNode({...node, text_draft_json});
            }}
            editorHeight="200px"
            includeFormatting={Boolean(richTextEditorFormattingEnabled)}
            includeHyperlink={!chatbotNewHyperlinkModalEnabled}
            includeHyperlinkV2={Boolean(chatbotNewHyperlinkModalEnabled)}
            includeGif
            // @ts-expect-error - TS2322 - Type '{ key: number; label: string; name: string; node_id: number; node_name: string; derived_field_name?: string | undefined; template: string; node_type?: string | undefined; attribute_name?: string | undefined; }[]' is not assignable to type 'MenuOption[]'.
            nodeVariables={mappedNodeVariables}
            atsVariables={
              graph.use_case === 'sourcing' ? undefined : chatbotDynamicLabels
            }
            readOnly={isFieldReadOnly('text_draft_json')}
            entityType={graph.anchor_entity_type}
            prepareContentOnLoad={prepareContentOnLoad}
            atsTooltip={
              hasAncestorJobMatchQuestionNode
                ? t('ATS_External_variables', 'ATS & External variables')
                : t('ATS_variables', 'ATS variables')
            }
            enableDraftJSONCopy
          />
        ) : (
          <MessageDraftEditor
            placeholder={`${t(
              'I\'d_like_to_schedule_a_meeting_with_you_Select_times_that_work_for_you',
              'I\'d like to schedule a meeting with you. Select times that work for you',
            )}.`}
            defaultValue={node.text_draft_json}
            onChange={(text_draft_json) => setNode({...node, text_draft_json})}
            entityType={graph.anchor_entity_type}
            isFirstSmsMessage={isFirstSmsMessage}
            label=""
            extraCharacterCount={0}
            useCase={graph.use_case}
            nodeVariables={nodeVariables}
            readOnly={isFieldReadOnly('text_draft_json')}
            showDiscoverAttributes={hasAncestorJobMatchQuestionNode}
            showExternalVariables={Boolean(graph.flow_config.api_bot_enabled)}
            warningMessage={
              isUsingDiscoverAttribute ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE : ''
            }
          />
        )}
        {error?.text_draft_json && (
          <div className={css.errorText}>
            {t('Message_cannot_be_empty', 'Message cannot be empty')}.
          </div>
        )}
      </div>
      <div className={css.collapsibleHeader}>{t('Calendar', 'Calendar')}</div>
      <div className={css.section}>
        <p className={css.description}>
          {`${t('Select_a_team', 'Select a team')}${
            schedulerNodeEnhancementEnabled
              ? ` ${t('or_dynamic', 'or dynamic')}`
              : ''
          } ${t(
            'template_for_this_meeting_scheduler_You_cannot_use_personal_templates_for_this_node_Then_map_the_template_questions_to_a_node_value_or_a_variable',
            'template for this meeting scheduler. You cannot use personal templates for this node. Then, map the template questions to a node value or a variable',
          )}.`}
        </p>

        {schedulerNodeEnhancementEnabled && (
          <>
            <div className={classify(css.inputLabel, css.withMargin)}>
              {t('Select_one', 'Select one')}
            </div>
            <Select
              disabled={readOnly}
              value={templateTypeOptions.find(
                ({value}) => value === node.scheduler_data.type,
              )}
              options={templateOptions}
              onChange={(selected) => {
                if (selected) {
                  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
                  setVariableTemplateStatus();
                  setNode({
                    ...node,
                    scheduler_template_id: null,
                    scheduler_data: {
                      ...node.scheduler_data,
                      type: selected.value,
                      global_variable: null,
                      additional_questions: [],
                    },
                  });
                }
              }}
              hasLabel
              resolveKey={(option) => option.value}
              components={{
                ValueComponent: () => (
                  <div>
                    {
                      templateTypeOptions.find(
                        ({value}) => value === node.scheduler_data.type,
                      )?.label
                    }
                  </div>
                ),
              }}
              resolveLabel={(option) => (
                <div className={css.column}>
                  <div>{t(makeKey(option.label), option.label)}</div>
                  <div>
                    {t(makeKey(option.description), option.description)}
                  </div>
                </div>
              )}
              error={error?.scheduler_template_id}
            />
          </>
        )}

        {chatbotMultiHostMeeting &&
          node.scheduler_data.type === 'static_template' && (
            <>
              <div className={classify(css.inputLabel, css.withMargin)}>
                {t('Template_Type', 'Template Type')}
              </div>
              {/* @ts-expect-error - TS2739 - Type '{ disabled: boolean; value: { value: string; label: string; } | undefined; options: { value: string; label: string; }[]; onChange: (selected: { value: string; label: string; } | null | undefined) => void; }' is missing the following properties from type 'SenseTypeaheadProps<{ value: string; label: string; }>': resolveKey, resolveLabel */}
              <Select
                disabled={readOnly}
                value={distributionRuleTypes.find(({value}) =>
                  value.includes(distributionFilter),
                )}
                options={distributionRuleTypes}
                onChange={(selected) => {
                  if (selected) {
                    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'SetStateAction<"single_account" | "round_robin_availability" | "round_robin_fairness" | "panel_interview" | "one_event_multiple_attendee" | "one_slot_multiple_event">'.
                    setDistributionFilter(selected.value);
                    setNode({
                      ...node,
                      scheduler_template_id: null,
                      scheduler_data: {
                        ...node.scheduler_data,
                        contacts: [],
                      },
                    });
                  }
                }}
              />
            </>
          )}

        {((schedulerNodeEnhancementEnabled &&
          node.scheduler_data.type === 'static_template') ||
          !schedulerNodeEnhancementEnabled) && (
          <>
            <div className={classify(css.inputLabel, css.withMargin)}>
              {t('Select_a_template', 'Select a template')}
            </div>
            <Select
              searchable
              clearable
              disabled={readOnly}
              value={
                node.scheduler_template_id
                  ? // this .get lookup will fail if the user does not have permission
                    // to access multi-event-templates which are set by 'admin' users
                    // as defined by scheduler: i.e. users that can edit 'settings'
                    templatesMap.get(node.scheduler_template_id) ?? {
                      id: node.scheduler_template_id ?? '',
                      name: node.scheduler_template_id
                        ? `[${t(
                            'Template_only_visible_to_administrators',
                            'Template only visible to administrators',
                          )}]`
                        : '',
                    }
                  : null
              }
              options={filteredTemplates}
              onChange={(template) =>
                setNode({
                  ...node,
                  scheduler_template_id: template?.id,
                  scheduler_data: {
                    ...node.scheduler_data,
                    contacts: [],
                  },
                })
              }
              resolveLabel={(template) => template.name}
              resolveKey={(template) => template.id}
              error={error?.scheduler_template_id}
            />
            {error?.scheduler_template_id && (
              <div className={css.errorText}>
                {t('Template_cannot_be_empty', 'Template cannot be empty')}.
              </div>
            )}
          </>
        )}

        {schedulerNodeEnhancementEnabled &&
          node.scheduler_data.type === 'dynamic_template' && (
            <>
              <div className={classify(css.inputLabel, css.withMargin)}>{`${t(
                'Select_template_mapping_variable',
                'Select template mapping variable',
              )}:`}</div>
              <Select
                searchable
                disabled={readOnly}
                value={
                  //this could be nullish, need to check after types are in a good place
                  node.scheduler_data.global_variable?.name
                    ? globalVariablesByLabel[
                        node.scheduler_data.global_variable.name
                      ]
                    : null
                }
                options={mappedGloablVariables}
                onChange={handleSelectGloablVariable}
                resolveLabel={(template) => template.label}
                resolveKey={(template) => `${template.id}_${template.label}`}
                error={error?.scheduler_mapping_var}
              />
              {variableTemplateStatus === 'error' && (
                <div className={css.errorText}>{`${t(
                  'Global_variable_does_not_have_any_template_mappings',
                  'Global variable does not have any template mappings',
                )}.`}</div>
              )}
            </>
          )}
        {variableTemplateStatus === 'loading' && (
          <div className={css.loading}>
            <Loading />
            {t('Loading_questions', 'Loading questions')}
          </div>
        )}
      </div>
      {showTemplateQuestions && (
        <div className={classify(css.section, css.padded)}>
          {node.scheduler_data.questions.map((question, index) => (
            <QuestionPicker
              key={question.name}
              question={question}
              nodes={nodes}
              entityType={graph.anchor_entity_type}
              readOnly={readOnly}
              onChange={(nextQuestion) => {
                setNode({
                  ...node,
                  scheduler_data: {
                    ...node.scheduler_data,
                    questions: node.scheduler_data.questions.map(
                      (currentQuestion) =>
                        question === currentQuestion
                          ? nextQuestion
                          : currentQuestion,
                    ),
                  },
                });
              }}
              error={error?.scheduler_template_questions?.[String(index)]}
              type={question.name}
              variableType={isSourcingFlow ? 'node' : 'all'}
            />
          ))}
          {node.scheduler_data.additional_questions.map((question, index) => (
            <QuestionPicker
              key={question.name}
              question={question}
              nodes={nodes}
              entityType={graph.anchor_entity_type}
              readOnly={readOnly}
              onChange={(nextQuestion) => {
                setNode({
                  ...node,
                  scheduler_data: {
                    ...node.scheduler_data,
                    additional_questions:
                      node.scheduler_data.additional_questions.map(
                        (currentQuestion) =>
                          question === currentQuestion
                            ? nextQuestion
                            : currentQuestion,
                      ),
                  },
                });
              }}
              type={
                node.scheduler_template_id
                  ? keyBy(
                      templatesMap.get(node.scheduler_template_id)
                        ?.additional_questions ?? [],
                      'question',
                    ).get(question.name)?.answer_type
                  : undefined
              }
              error={
                error?.scheduler_template_additional_questions?.[String(index)]
              }
              variableType={isSourcingFlow ? 'node' : 'all'}
            />
          ))}
        </div>
      )}
      {schedulerNodeEnhancementEnabled && (
        <>
          <div className={css.collapsibleHeader}>
            {t('Meeting_Details', 'Meeting Details')}
          </div>

          <div className={classify(css.section, css.padded)}>
            <p className={css.description}>{`${t(
              'Meeting_details_will_display_on_the_calendar_invite',
              'Meeting details will display on the calendar invite',
            )}.`}</p>
          </div>

          <div className={css.section}>
            <div className={css.inputLabel}>
              {t('Meeting_title', 'Meeting title')}
            </div>
            <MessageDraftEditor
              label=""
              placeholder={`${t(
                '1_hr_Meeting_with',
                '1 hr Meeting with',
              )} <recruiter.name>`}
              defaultValue={
                node.scheduler_data.event_info?.event_title ??
                createRawDraftContentState()
              }
              onChange={(text_draft_json) =>
                setNode({
                  ...node,
                  scheduler_data: {
                    ...node.scheduler_data,
                    event_info: {
                      event_description:
                        node.scheduler_data.event_info?.event_description,
                      event_title: text_draft_json,
                    },
                  },
                })
              }
              entityType={graph.anchor_entity_type}
              isFirstSmsMessage={isFirstSmsMessage}
              error={
                error?.event_info &&
                (!node.scheduler_data.event_info?.event_title ||
                  (node.scheduler_data.event_info.event_title &&
                    !hasText(node.scheduler_data.event_info.event_title)))
                  ? error.event_info
                  : undefined
              }
              readOnly={isFieldReadOnly('event_info.event_title')}
              useCase={graph.use_case}
              nodeVariables={nodeVariables}
              allowGifs={false}
              showDiscoverAttributes={hasAncestorJobMatchQuestionNode}
              showExternalVariables={Boolean(graph.flow_config.api_bot_enabled)}
              warningMessage={
                isUsingDiscoverAttributeInTitle
                  ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE
                  : ''
              }
            />

            <div className={classify(css.inputLabel, css.withMargin)}>
              {t('Meeting_details', 'Meeting details')}
            </div>
            <RichEditorWithWarning
              warningMessage={
                isUsingDiscoverAttributeInDetails
                  ? DISCOVER_ATTRIBUTE_WARNING_MESSAGE
                  : ''
              }
              defaultValue={
                node.scheduler_data.event_info?.event_description ??
                createRawDraftContentState()
              }
              onChange={(text_draft_json) =>
                setNode({
                  ...node,
                  scheduler_data: {
                    ...node.scheduler_data,
                    event_info: {
                      event_title: node.scheduler_data.event_info?.event_title,
                      event_description: text_draft_json,
                    },
                  },
                })
              }
              // @ts-expect-error - TS2322 - Type '{ key: number; label: string; name: string; node_id: number; node_name: string; derived_field_name?: string | undefined; template: string; node_type?: string | undefined; attribute_name?: string | undefined; }[]' is not assignable to type 'MenuOption[]'.
              nodeVariables={mappedNodeVariables}
              atsVariables={
                graph.use_case === 'sourcing' ? undefined : chatbotDynamicLabels
              }
              editorHeight="200px"
              includeFormatting
              includeHyperlink={!chatbotNewHyperlinkModalEnabled}
              includeHyperlinkV2={Boolean(chatbotNewHyperlinkModalEnabled)}
              entityType={graph.anchor_entity_type}
              readOnly={isFieldReadOnly('event_info.event_description')}
              prepareContentOnLoad={prepareContentOnLoad}
              atsTooltip={
                hasAncestorJobMatchQuestionNode
                  ? `${t('ATS_External_variables', 'ATS & External variables')}`
                  : `${t('ATS_variables', 'ATS variables')}`
              }
              enableDraftJSONCopy
            />
            {meetingDetailsErrorMessage && (
              <div className={css.errorText}>{meetingDetailsErrorMessage}</div>
            )}
          </div>
        </>
      )}
      {!isSourcingFlow && (
        <>
          <div className={css.collapsibleHeader}>{`${t(
            'Host_optional',
            'Host (optional)',
          )}`}</div>

          <div className={classify(css.section, css.padded)}>
            <p className={css.description}>{`${t(
              'The_primary_host_will_be_prioritized_to_be_scheduled_for_meetings_over_secondary_host_and_other_team_members_in_the_selected_template',
              'The primary host will be prioritized to be scheduled for meetings, over secondary host and other team members in the selected template',
            )}.`}</p>

            <div className={css.column}>
              <div className={css.inputLabel}>
                {isSelectedTemplatePanel
                  ? `${t('Primary_attendees', 'Primary attendees')}`
                  : `${t(
                      'Primary_meeting_host_optional',
                      'Primary meeting host (optional)',
                    )}`}
              </div>

              {!isSelectedTemplatePanel && (
                // @ts-expect-error - TS2739 - Type '{ baseEntityType: EntityType; searchable: true; autofocus: true; placeholder: string; limit: number; values: any[]; options: any[]; onChange: (options: any[]) => void; disabled: boolean; }' is missing the following properties from type 'SenseTypeaheadProps<any>': resolveKey, resolveLabel
                <VariablePickerSelect
                  baseEntityType={graph.anchor_entity_type}
                  searchable
                  autofocus
                  placeholder={`${t('Search_variables', 'Search variables')}`}
                  limit={1}
                  values={[
                    dynamicLabelMap.get(
                      node.scheduler_data.contacts.find(
                        (d) => d.name === 'primary',
                      )?.dynamic_field,
                    ),
                  ].filter(Boolean)}
                  options={dynamicLabels}
                  onChange={(options) => {
                    const fieldValue = options[0]?.value;
                    const stub = {
                      dynamic_field: fieldValue,
                      name: 'primary',
                      node_id: null,
                    } as const;
                    const contactList = node.scheduler_data.contacts.filter(
                      (c) => c.name !== 'primary',
                    );

                    setNode({
                      ...node,
                      scheduler_data: {
                        ...node.scheduler_data,
                        contacts:
                          options.length === 0
                            ? contactList
                            : [...contactList, stub],
                      },
                    });
                  }}
                  disabled={readOnly}
                />
              )}

              {isSelectedTemplatePanel &&
                primaryPanelAttendees.length === 0 && (
                  <AttendeeRow
                    contacts={primaryPanelAttendees}
                    handleUpdateContact={handleUpdateContact}
                    handleRemoveAttendee={handleRemoveAttendee}
                    options={normalizedDynamicLabels}
                    isPrimary
                    index={null}
                  />
                )}

              {isSelectedTemplatePanel &&
                primaryPanelAttendees.map((contact, index) => (
                  <AttendeeRow
                    contacts={primaryPanelAttendees}
                    // @ts-expect-error - TS2322 - Type 'SchedulerDatum' is not assignable to type 'null | undefined'.
                    contact={contact}
                    handleUpdateContact={handleUpdateContact}
                    handleRemoveAttendee={handleRemoveAttendee}
                    options={normalizedDynamicLabels}
                    isPrimary
                    index={index}
                  />
                ))}

              {isSelectedTemplatePanel &&
                primaryPanelAttendees.length > 0 &&
                primaryPanelAttendees[primaryPanelAttendees.length - 1]
                  .dynamic_field && (
                  <div
                    className={css.addAttendee}
                    onClick={() => handleAddPanelAttendee(true)}
                  >{`+ ${t('Add_attendee', 'Add attendee')}`}</div>
                )}
            </div>

            <div className={css.column}>
              <div className={css.inputLabel}>
                {isSelectedTemplatePanel
                  ? `${t('Secondary_attendees', 'Secondary attendees')}`
                  : `${t(
                      'Secondary_meeting_host_optional',
                      'Secondary meeting host (optional)',
                    )}`}
              </div>
              {!isSelectedTemplatePanel && (
                // @ts-expect-error - TS2739 - Type '{ baseEntityType: EntityType; searchable: true; placeholder: string; limit: number; values: any[]; options: any[]; onChange: (options: any[]) => void; disabled: boolean; }' is missing the following properties from type 'SenseTypeaheadProps<any>': resolveKey, resolveLabel
                <VariablePickerSelect
                  baseEntityType={graph.anchor_entity_type}
                  searchable
                  placeholder={`${t('Search_variables', 'Search variables')}`}
                  limit={1}
                  values={[
                    dynamicLabelMap.get(
                      node.scheduler_data.contacts.find(
                        (d) => d.name === 'secondary',
                      )?.dynamic_field,
                    ),
                  ].filter(Boolean)}
                  options={dynamicLabels}
                  onChange={(options) => {
                    const fieldValue = options[0]?.value;
                    const stub = {
                      dynamic_field: fieldValue,
                      name: 'secondary',
                      node_id: null,
                    } as const;
                    const contactList = node.scheduler_data.contacts.filter(
                      (c) => c.name !== 'secondary',
                    );

                    setNode({
                      ...node,
                      scheduler_data: {
                        ...node.scheduler_data,
                        contacts:
                          options.length === 0
                            ? contactList
                            : [...contactList, stub],
                      },
                    });
                  }}
                  disabled={readOnly}
                />
              )}

              {isSelectedTemplatePanel &&
                secondaryPanelAttendees.length === 0 && (
                  <AttendeeRow
                    contacts={secondaryPanelAttendees}
                    handleUpdateContact={handleUpdateContact}
                    handleRemoveAttendee={handleRemoveAttendee}
                    options={normalizedDynamicLabels}
                    isPrimary={false}
                    index={null}
                  />
                )}

              {isSelectedTemplatePanel &&
                secondaryPanelAttendees.map((contact, index) => (
                  <AttendeeRow
                    contacts={secondaryPanelAttendees}
                    // @ts-expect-error - TS2322 - Type 'SchedulerDatum' is not assignable to type 'null | undefined'.
                    contact={contact}
                    handleUpdateContact={handleUpdateContact}
                    handleRemoveAttendee={handleRemoveAttendee}
                    options={normalizedDynamicLabels}
                    isPrimary={false}
                    index={index}
                  />
                ))}

              {isSelectedTemplatePanel &&
                secondaryPanelAttendees.length > 0 &&
                secondaryPanelAttendees[secondaryPanelAttendees.length - 1]
                  .dynamic_field && (
                  <div
                    className={css.addAttendee}
                    onClick={() => handleAddPanelAttendee(false)}
                  >{`+ ${t('Add_attendee', 'Add attendee')}`}</div>
                )}
            </div>

            {/* disable_template_fallback */}
            {isSelectedTemplatePanel && (
              <div className={css.column}>
                <div className={css.inputLabel}>
                  {t('Template_Fallback', 'Template Fallback')}
                </div>
                <p
                  className={classify(css.description, css.warningParagraph)}
                >{`${t(
                  'When_the_toggle_is_enabled_scheduler_will_not_fall_back_to_the_template_attendees_or_host_but_will_only_look_for_availability_from_primary_and_secondary_attendeeshost',
                  'When the toggle is enabled, scheduler will not fall back to the template attendees or host, but will only look for availability from primary and secondary attendees/host',
                )}.`}</p>
                <GenesisToggle
                  labelPosition="right"
                  name="disable_template_fallback"
                  onChange={(e) =>
                    setNode({
                      ...node,
                      scheduler_data: {
                        ...node.scheduler_data,
                        use_template_hosts: !e.checked,
                      },
                    })
                  }
                  checked={!node.scheduler_data.use_template_hosts}
                >
                  {t('Disable_Template_Fallback', 'Disable Template Fallback')}
                </GenesisToggle>
              </div>
            )}
          </div>
        </>
      )}
      <div className={css.footer}>
        <Button size="small" type="secondary" onClick={onCancel}>
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleSaveNode}
          disabled={hasSubmitted && !!validation}
        >
          {canFlowUpdate ? t('Update', 'Update') : t('Save', 'Save')}
        </Button>
      </div>
    </div>
  );
}

function QuestionPicker({
  question,
  nodes,
  onChange,
  readOnly = false,
  entityType,
  error,
  type,
  variableType = 'all',
}: {
  question: SchedulerDatum;
  nodes: ConversationNode[];
  onChange: (question: SchedulerDatum) => void;
  readOnly?: boolean;
  entityType: EntityType;
  error?: Validation | null | undefined;
  type?: SchedulerAnswerType | string;
  variableType?: 'all' | 'ats' | 'node';
}) {
  const {t, makeKey} = useLabelTranslation();
  const [valueType, setValueType] = React.useState<'ats' | 'node'>(
    variableType !== 'all' ? variableType : question.node_id ? 'node' : 'ats',
  );

  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const inputRef = React.useRef();
  const handleButtonClick = (type: string) => {
    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'SetStateAction<"node" | "ats">'.
    setValueType(type);
    setTimeout(() => {
      // @ts-expect-error - TS2339 - Property 'focus' does not exist on type '{}'.
      inputRef.current?.focus();
    }, 0);
  };

  const nodeOptions = type
    ? nodes.filter((node) => {
        // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'ConversationNode'.
        if (node.answers || type === 'dropdown') {
          // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'ConversationNode'.
          const isDropdownNode = node.answers && type === 'dropdown';
          const isBooleanNode =
            // @ts-expect-error - TS2339 - Property 'response_type' does not exist on type 'ConversationNode'.
            node.response_type &&
            // @ts-expect-error - TS2339 - Property 'response_type' does not exist on type 'ConversationNode'.
            node.response_type === 'boolean' &&
            // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly phone: "phone number"; readonly email: "email"; readonly name: "string"; readonly number: "number"; readonly checkbox: "boolean"; readonly consent: "boolean"; readonly multi_line_text: "string"; readonly dropdown: "string"; }'.
            validationTypeByQuestionType[type] === 'boolean';

          return isDropdownNode || isBooleanNode;
          // @ts-expect-error - TS2339 - Property 'response_type' does not exist on type 'ConversationNode'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly phone: "phone number"; readonly email: "email"; readonly name: "string"; readonly number: "number"; readonly checkbox: "boolean"; readonly consent: "boolean"; readonly multi_line_text: "string"; readonly dropdown: "string"; }'.
        } else if (node.response_type && validationTypeByQuestionType[type]) {
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly phone: "phone number"; readonly email: "email"; readonly name: "string"; readonly number: "number"; readonly checkbox: "boolean"; readonly consent: "boolean"; readonly multi_line_text: "string"; readonly dropdown: "string"; }'. | TS2339 - Property 'response_type' does not exist on type 'ConversationNode'.
          return validationTypeByQuestionType[type] === node.response_type;
        } else {
          //no validation requirement, default to true
          return true;
        }
      })
    : nodes;

  const questionText = getQuestionText(t);

  return (
    <div key={question.name} className={css.column}>
      <div className={css.inputLabel}>
        {/* @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ name: string; email: string; purpose: string; phone: string; }'. */}
        {questionText[question.name] ??
          t(makeKey(question.name), question.name)}
      </div>
      <div>
        {valueType === 'node' ? (
          <Select
            classNames={{container: css.columnInput}}
            searchable
            clearable
            options={nodeOptions}
            value={nodes.find((node) => node.id === question.node_id)}
            resolveLabel={(node) => node.name}
            onChange={(node) =>
              onChange({
                ...question,
                node_id: node?.id,
                dynamic_field: null,
              })
            }
            disabled={readOnly}
            placeholder={`${t('Select_a_node_value', 'Select a node value')}`}
            // @ts-expect-error - TS17001 - JSX elements cannot have multiple attributes with the same name.
            resolveLabel={(node) => node.name}
            resolveKey={(node) => String(node.id)}
            error={error}
          />
        ) : (
          <AllVariablePicker
            className={css.columnInput}
            entityType={entityType}
            type={type}
            value={question.dynamic_field}
            onChange={(attribute) => {
              onChange({
                ...question,
                dynamic_field: attribute?.value,
                node_id: null,
              });
            }}
            readOnly={readOnly}
            placeholder={`${t('Select_a_var', 'Select a var')}`}
            hasLabel
            inputProps={{
              // @ts-expect-error - TS2322 - Type 'RefObject<unknown>' is not assignable to type 'Ref<HTMLInputElement> | undefined'.
              ref: inputRef,
              autoComplete: 'sense-entity-attribute',
            }}
            error={error}
          />
        )}
        {!readOnly && variableType === 'all' && (
          <Toggle
            value={valueType === 'ats'}
            onChange={(isVariable) =>
              handleButtonClick(isVariable ? 'ats' : 'node')
            }
          />
        )}
      </div>
      {error && (
        <div className={css.errorText}>
          {t('Please_select_a_value', 'Please select a value')}
        </div>
      )}
    </div>
  );
}

const AttendeeRow = ({
  contact = null,
  // @ts-expect-error - TS7031 - Binding element 'contacts' implicitly has an 'any' type.
  contacts,
  // @ts-expect-error - TS7031 - Binding element 'handleUpdateContact' implicitly has an 'any' type.
  handleUpdateContact,
  // @ts-expect-error - TS7031 - Binding element 'handleRemoveAttendee' implicitly has an 'any' type.
  handleRemoveAttendee,
  // @ts-expect-error - TS7031 - Binding element 'options' implicitly has an 'any' type.
  options,
  // @ts-expect-error - TS7031 - Binding element 'isPrimary' implicitly has an 'any' type.
  isPrimary,
  // @ts-expect-error - TS7031 - Binding element 'index' implicitly has an 'any' type.
  index,
}) => {
  const {t} = useLabelTranslation();
  const primaryPanelAttendeeTypes = [
    {key: 'primary_panel_host', label: t('Host', 'Host')},
    {key: 'primary_panel_required', label: t('Required', 'Required')},
    {key: 'primary_panel_optional', label: t('Optional', 'Optional')},
  ];
  const secondaryPanelAttendeeTypes = [
    {key: 'secondary_panel_host', label: t('Host', 'Host')},
    {key: 'secondary_panel_required', label: t('Required', 'Required')},
    {key: 'secondary_panel_optional', label: t('Optional', 'Optional')},
  ];
  const attendeeTypeOptions = isPrimary
    ? primaryPanelAttendeeTypes
    : secondaryPanelAttendeeTypes;
  // @ts-expect-error - TS7031 - Binding element 'dynamic_field' implicitly has an 'any' type.
  const contactsFields = contacts.map(({dynamic_field}) => dynamic_field);
  const filteredOptions = options.filter(
    // @ts-expect-error - TS7006 - Parameter 'option' implicitly has an 'any' type.
    (option) =>
      !contactsFields.includes(option.key) ||
      // @ts-expect-error - TS2339 - Property 'dynamic_field' does not exist on type 'never'.
      option.key === contact?.dynamic_field,
  );

  // this code is used to handle cases where a team template is selected but name field is primary/secondary
  const selectedAttendeeType: Array<string> = [];
  // @ts-expect-error - TS2339 - Property 'name' does not exist on type 'never'.
  if (contact?.name) {
    // @ts-expect-error - TS2339 - Property 'name' does not exist on type 'never'.
    switch (contact.name) {
      case 'primary':
        selectedAttendeeType.push('primary_panel_host');
        break;
      case 'secondary':
        selectedAttendeeType.push('secondary_panel_host');
        break;
      default:
        // @ts-expect-error - TS2339 - Property 'name' does not exist on type 'never'.
        selectedAttendeeType.push(contact.name);
    }
  }

  return (
    <div className={css.attendeeRow}>
      <SimpleDropdown
        options={filteredOptions}
        staticLabels={{
          SEARCH_PLACEHOLDER: `${t('Search', 'Search')}...`,
          RESULT: t('result', 'result'),
          RESULTS: t('results', 'results'),
        }}
        onChange={(selected) => {
          handleUpdateContact(
            index,
            selected.key,
            isPrimary ? 'primary_panel_host' : 'secondary_panel_host',
            isPrimary,
          );
          return;
        }}
        // @ts-expect-error - TS2339 - Property 'dynamic_field' does not exist on type 'never'. | TS2339 - Property 'dynamic_field' does not exist on type 'never'.
        selectedKeys={contact?.dynamic_field ? [contact.dynamic_field] : []}
        allowSearch
        placeholder={`${t('Select', 'Select')}...`}
      />
      <SimpleDropdown
        options={attendeeTypeOptions}
        onChange={(selected) => {
          handleUpdateContact(
            index,
            // @ts-expect-error - TS2339 - Property 'dynamic_field' does not exist on type 'never'.
            String(contact?.dynamic_field),
            selected.key,
            isPrimary,
          );
        }}
        selectedKeys={selectedAttendeeType}
        allowSearch
        disabled={!contact}
        placeholder={`${t('Select', 'Select')}...`}
        staticLabels={{
          SEARCH_PLACEHOLDER: `${t('Search', 'Search')}...`,
          RESULT: t('result', 'result'),
          RESULTS: t('results', 'results'),
        }}
      />
      {contacts.length > 1 && (
        <CloseIcon
          ariaLabel={t('Close_Icon', 'Close Icon')}
          color="primary"
          onClick={(e) => handleRemoveAttendee(index)}
          size="small"
          type="regular"
        />
      )}
    </div>
  );
};
